import React, { useState, useEffect } from "react";
import {
  doSignInWithEmailAndPassword,
  doSignInWithGoogle,
} from "../../firebase/auth";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { userTypeState } from "../../Recoil/atoms";

const LoginPopUp = ({ showPopUp, onClose }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "password":
        if (!value) {
          error = "Password is required";
        } else if (value.length < 6) {
          error = "Password must be at least 6 characters long";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const navigate = useNavigate();
  const [userType, setUserType] = useRecoilState(userTypeState);

  const handleInputChange = (field, value) => {
    // Update the field value
    if (field === "email") {
      setEmail(value);
    } else if (field === "password") {
      setPassword(value);
    }

    // Validate and set error
    const error = validateField(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const emailError = validateField("email", email);
    const passwordError = validateField("password", password);

    setErrors({
      email: emailError,
      password: passwordError,
    });

    // If there are any errors, don't submit
    if (emailError || passwordError) {
      return;
    }

    try {
      await doSignInWithEmailAndPassword(email, password);
      onClose();
    } catch (error) {
      // Handle Firebase specific errors
      switch (error.code) {
        case "auth/user-not-found":
          setErrors((prev) => ({
            ...prev,
            email: "No account found with this email",
          }));
          break;
        case "auth/wrong-password":
          setErrors((prev) => ({
            ...prev,
            password: "Incorrect password",
          }));
          break;
        case "auth/invalid-email":
          setErrors((prev) => ({
            ...prev,
            email: "Invalid email format",
          }));
          break;
        case "auth/user-disabled":
          setErrors((prev) => ({
            ...prev,
            email: "This account has been disabled",
          }));
          break;
        case "auth/too-many-requests":
          setErrors((prev) => ({
            ...prev,
            password: "Too many attempts. Please try again later",
          }));
          break;
        default:
          console.error("Login failed:", error);
          setErrors((prev) => ({
            ...prev,
            password: "Login failed. Please try again",
          }));
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const response = await doSignInWithGoogle();
      console.log("Google Sign In successful:", response);
      if (response.newUser) {
        // Redirect new user to select role
        navigate("/select-role");
      } else {
        // Existing user, set global userType
        setUserType(response.role);
        navigate("/home");
      }
      onClose();
    } catch (error) {
      console.error("Google Sign In failed:", error);
      setErrors((prev) => ({
        ...prev,
        email: "Google sign in failed. Please try again",
      }));
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (showPopUp && !e.target.closest(".bg-white")) {
        onClose();
      }
    };

    if (showPopUp) {
      // Add a small delay to prevent immediate triggering
      const timeoutId = setTimeout(() => {
        document.body.addEventListener("click", handleOutsideClick);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.body.removeEventListener("click", handleOutsideClick);
      };
    }

    return () => {
      document.body.removeEventListener("click", handleOutsideClick);
    };
  }, [showPopUp, onClose]);

  return (
    <div
      className={`fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 transition duration-300 ease-in-out ${
        showPopUp
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-white rounded-lg p-8 shadow-md relative w-96">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-0 right-0 m-4 focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-500 hover:text-gray-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <h2 className="text-2xl font-bold mb-6">Login</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring focus:ring-indigo-500 focus:ring-opacity-50 ${
                errors.email ? "border-red-500" : ""
              }`}
              value={email}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
          <div className="mb-6">
            <label
              htmlFor="password"
              className="block text-sm font-medium mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring focus:ring-indigo-500 focus:ring-opacity-50 ${
                errors.password ? "border-red-500" : ""
              }`}
              value={password}
              onChange={(e) => handleInputChange("password", e.target.value)}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>
          <div className="flex">
            <button
              type="submit"
              className="bg-indigo-500 text-white hover:bg-indigo-700 font-bold py-2 px-4 rounded-md focus:outline-none focus:ring focus:ring-indigo-500 focus:ring-opacity-50 w-full mr-2"
            >
              Login
            </button>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="bg-red-600 text-white hover:bg-red-700 font-bold py-2 px-4 rounded-md focus:outline-none focus:ring focus:ring-red-500 focus:ring-opacity-50 w-full ml-2"
            >
              Google Sign In
            </button>
          </div>
        </form>
        <div className="text-sm text-gray-500 hover:text-gray-700 mt-6 block cursor-pointer">
          Forgot Password?
        </div>
      </div>
    </div>
  );
};

export default LoginPopUp;
